import type { AxiosError, AxiosResponse } from 'axios';
import { z } from 'zod';

import type { Approver } from '../components/organisms/createTeamModal/peopleTable';
import type { CardProvider, UseGetTeamCardSourcesQuery } from '../queries/paymentQuery';
import { setTime } from '../utils/setTime';
import type { CreditCard, RequestType } from '../zustandStore/slices';

import HttpRepository from './httpRepository';
import UserRepository from './userRepository';
import type { Card } from './walletRepository';

export type CardSource = {
	cardSourceId: string;
	name?: string;
	teamId?: string | undefined;
};

type EnrollmentToken = {
	teamId?: string;
};

export type GetTeamApproversParams = {
	excludeSelf: boolean;
	excludeOutOfOffice?: boolean;
	teamId: string;
};

type PaymentInformation = {
	enrollmentToken: string;
	paymentInformation: any;
};

const BillingAddressSchema = z.object({
	address: z.union([z.null(), z.string()]).optional(),
	addressLine1: z.union([z.null(), z.string()]).optional(), // on get card for wallet
	city: z.union([z.null(), z.string()]).optional(),
	countryCode: z.union([z.null(), z.string()]).optional(),
	regionCode: z.union([z.null(), z.string()]).optional(),
	region: z.union([z.null(), z.string()]).optional(), // on get card for wallet
	zipCode: z.union([z.null(), z.string()]).optional(),
});
export type BillingAddress = z.infer<typeof BillingAddressSchema>;

const InfoSchema = z.object({
	balance: z.union([z.number(), z.null()]).optional(),
	billingAddress: z.union([BillingAddressSchema, z.null()]).optional(),
	cardType: z.union([z.null(), z.string()]).optional(),
	currency: z.union([z.null(), z.string()]).optional(),
	id: z.union([z.null(), z.string()]).optional(),
	provider: z.union([z.null(), z.string()]).optional(),
	validEndingOn: z.union([z.null(), z.string()]).optional(),
	value: z.union([z.number(), z.null()]).optional(),
	owner: z.union([z.null(), z.string()]).optional(),
	sourceId: z.union([z.null(), z.string()]).optional(),
	status: z.union([z.null(), z.string()]).optional(),
	frequency: z.union([z.null(), z.string()]).optional(),
	createdAt: z.union([z.null(), z.string()]).optional(),
	cardNumber: z.union([z.null(), z.string()]).optional(),
});
type Info = z.infer<typeof InfoSchema>;

const _VirtualCardData = z.object({
	cvv: z.union([z.null(), z.string()]).optional(),
	expiry: z.union([z.null(), z.string()]).optional(),
	expirationDate: z.union([z.null(), z.string()]).optional(),
	billingAddress: z.union([BillingAddressSchema, z.null()]).optional(),
	info: z.union([InfoSchema, z.null()]).optional(),
	number: z.union([z.null(), z.string()]).optional(),
	provider: z.union([z.null(), z.string()]).optional(),
	cardType: z.union([z.null(), z.string()]).optional(),
	cardStatus: z.union([z.null(), z.string()]).optional(),
	status: z.union([z.null(), z.string()]).optional(),
	lastDigits: z.union([z.null(), z.string()]).optional(),
	missingReceipts: z.union([z.null(), z.number()]).optional(),
	balance: z.union([z.number(), z.null()]).optional(),
});

export type VirtualCardDataApiType = z.infer<typeof _VirtualCardData>;
export type VirtualCardDataType = Omit<VirtualCardDataApiType, 'info'> & Info & { expiryDate?: string };

export const PaymentRepository = {
	getCardSources: async (params: any, teamId?: string) => {
		const response: AxiosResponse = teamId
			? await HttpRepository.tradeshiftGo().get(`/rest/teams/${teamId}/cardsources`, params)
			: await HttpRepository.tradeshiftGo().get('/rest/payments/cardsources', params);
		return response.data;
	},

	setCardName: async ({ cardSourceId, name, teamId }: CardSource) => {
		const response: AxiosResponse<CardSource, AxiosError> =
			teamId == null
				? await HttpRepository.tradeshiftGo().put(`/rest/payments/cardsources/${cardSourceId}/name`, { name })
				: await HttpRepository.tradeshiftGo().put(`/rest/teams/${teamId}/cardsources/${cardSourceId}/name`, { name });
		return response.data;
	},

	addCardSourceToTeam: async ({ teamId, cardSourceId }: CardSource) => {
		const response: AxiosResponse<CardSource> = await HttpRepository.tradeshiftGo().put(
			`/rest/teams/${teamId}/cardsources/${cardSourceId}`,
		);
		return response.data;
	},

	removeCardSourceFromTeam: async ({ teamId, cardSourceId }: CardSource) => {
		const response: AxiosResponse<CardSource> = await HttpRepository.tradeshiftGo().delete(
			`/rest/teams/${teamId}/cardsources/${cardSourceId}`,
		);
		return response.data;
	},

	getEnrollmentToken: async ({ teamId }: EnrollmentToken) => {
		const response: AxiosResponse = teamId
			? await HttpRepository.tradeshiftGo().get(`/rest/teams/${teamId}/cardsources/enrollment`)
			: await HttpRepository.tradeshiftGo().get('/rest/payments/enrollment');
		return response.data;
	},

	enrollWithAmex: async (options: PaymentInformation) => {
		const isEnrollSourceCardsFintechCardsAmexEnabled =
			globalThis.config.ENROLL_SOURCE_CARDS_FINTECH_CARDS_SWITCH_ENABLED;
		const response: AxiosResponse = isEnrollSourceCardsFintechCardsAmexEnabled
			? await HttpRepository.fintechCards(options.enrollmentToken).post('/cardsources', options.paymentInformation)
			: await HttpRepository.payments(options.enrollmentToken).post('/amex/enrollment', options.paymentInformation);
		return response.data;
	},

	enrollWithHsbc: async (options: PaymentInformation) => {
		const response: AxiosResponse = await HttpRepository.fintechCards(options.enrollmentToken).post(
			'/cardsources',
			options.paymentInformation,
		);
		return response.data;
	},

	getTeamApprovers: async ({ excludeSelf, excludeOutOfOffice, teamId }: GetTeamApproversParams) => {
		const { data: approvers }: AxiosResponse<{ users: Approver[] }> = await HttpRepository.tradeshiftGo().get(
			`/rest/teams/${teamId}/approvers`,
			{
				params: {
					excludeOutOfOffice,
				},
			},
		);

		if (excludeSelf) {
			const user = await UserRepository.getUser();
			return approvers.users.filter(({ userId }) => userId !== user?.userId);
		}

		return approvers.users;
	},

	getCompanyCardSources: async (params: any) => {
		const response: AxiosResponse<CreditCard[], AxiosError> = await HttpRepository.tradeshiftGo().get(
			`/rest/payments/cardsources?includeTeamsCount=${params.includeTeamsCount}`,
		);
		return response.data;
	},

	getTeamCardSources: async (params: UseGetTeamCardSourcesQuery) => {
		if (params.teamId) {
			const response: AxiosResponse = await HttpRepository.tradeshiftGo().get(
				`/rest/teams/${params.teamId}/cardsources?includeTeamsCount=${params.includeTeamsCount}`,
			);
			return response.data;
		} else {
			return [];
		}
	},

	getTeamsForCardSource: async (cardSourceId: string) => {
		const response: AxiosResponse = await HttpRepository.tradeshiftGo().get(
			`/rest/payments/cardsources/${cardSourceId}/teams`,
		);
		return response.data;
	},

	getCardSource: async ({ cardSourceId, includeCardHolder, teamId }: CardSource & { includeCardHolder: boolean }) => {
		const response: AxiosResponse = teamId
			? await HttpRepository.tradeshiftGo().get(`/rest/teams/${teamId}/cardsources/${cardSourceId}`, {
					params: {
						includeCardHolder: !!includeCardHolder,
					},
				})
			: await HttpRepository.tradeshiftGo().get(`/rest/payments/cardsources/${cardSourceId}`, {
					params: {
						includeCardHolder: !!includeCardHolder,
					},
				});
		return response.data;
	},

	getCurrencies: async () => {
		const response: AxiosResponse = await HttpRepository.tradeshiftGo().get('/rest/payments/currencies');
		return response.data;
	},

	getVirtualCardToken: async (documentId: RequestType['requestId']) => {
		const response: AxiosResponse<
			{ token: string; tokenSource?: 'fintech-cards-amex' | 'tradeshift-cards' },
			AxiosError
		> = await HttpRepository.tradeshiftGo().get(`/rest/payments/card?documentId=${documentId}`);
		return response.data;
	},

	getVirtualCard: async (token: string, cardProvider: CardProvider | undefined, tokenSource?: string) => {
		if (!cardProvider) {
			throw new TypeError('cardProvider is required');
		}
		const useFintechCards = tokenSource === 'fintech-cards-amex';
		const response: AxiosResponse<VirtualCardDataApiType, AxiosError> = useFintechCards
			? await HttpRepository.fintechCards(token).get('/card', {
					timeout: setTime(10, 'seconds'),
					headers: { 'Cache-Control': 'no-cache' },
				})
			: await HttpRepository.payments(token).get('/card', { timeout: setTime(10, 'seconds') });
		return response.data;
	},

	getVirtualCardInfo: async (options: { documentId: string }) => {
		if (!options.documentId) {
			throw new Error('documentId is required');
		}
		const response: AxiosResponse<Card, AxiosError> = await HttpRepository.tradeshiftGo().get(
			`/rest/payments/card/info?documentId=${options.documentId}`,
		);
		return response.data;
	},
};
